<template>
    <div class="flex flex-col dark:text-white">
        <div class="my-5 flex items-end justify-between">
            <h1 class="text-2xl font-semibold">FDIC — Cedentes</h1>
            <router-link
                :to="{ name: 'fdic.batch' }"
                class="inline-flex items-center rounded-md bg-indigo-500 px-4 py-2 text-sm font-semibold leading-6 text-white shadow hover:bg-indigo-400"
            >
                Novo envio em lote
            </router-link>
        </div>

        <form
            class="my-4 flex items-center gap-3"
            @submit.prevent="fetch(1)"
        >
            <select
                v-model="filters.status"
                class="rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            >
                <option value="">Todos status</option>
                <option value="pending">Pendente</option>
                <option value="processing">Processando</option>
                <option value="approved">Aprovado</option>
                <option value="approved_simple">Aprovado Simples</option>
                <option value="approved_with_warnings">Aprovado com Alertas</option>
                <option value="rejected">Rejeitado</option>
                <option value="error">Erro</option>
            </select>
            <input
                v-model="filters.document"
                type="text"
                placeholder="CPF/CNPJ"
                class="rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
            <input
                v-model="filters.from"
                type="date"
                class="rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
            <input
                v-model="filters.to"
                type="date"
                class="rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
            <button
                type="submit"
                :disabled="loading"
                class="inline-flex items-center rounded-md bg-indigo-500 px-4 py-2 text-sm font-semibold leading-6 text-white shadow hover:bg-indigo-400"
            >
                Filtrar
            </button>
            <button
                @click.prevent="clearFilters"
                type="button"
                class="rounded-md border border-indigo-500 bg-transparent px-4 py-2 text-sm font-semibold text-indigo-500 hover:bg-indigo-500 hover:text-white"
            >
                Limpar
            </button>
        </form>

        <div class="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead class="bg-gray-50 dark:bg-gray-800">
                    <tr>
                        <th class="px-4 py-3 text-left text-xs font-semibold uppercase">Cedente</th>
                        <th class="px-4 py-3 text-left text-xs font-semibold uppercase">Documento</th>
                        <th class="px-4 py-3 text-left text-xs font-semibold uppercase">Status</th>
                        <th class="px-4 py-3 text-left text-xs font-semibold uppercase">Ticket</th>
                        <th class="px-4 py-3 text-left text-xs font-semibold uppercase">Últ. envio</th>
                        <th class="px-4 py-3"></th>
                    </tr>
                </thead>
                <tbody class="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
                    <tr v-for="item in items" :key="item.id">
                        <td class="px-4 py-3 text-sm">
                            <router-link
                                v-if="item.user"
                                :to="{ name: 'user.details', params: { user_id: item.user.id } }"
                                target="_blank"
                                class="text-indigo-500 hover:underline"
                            >
                                {{ item.user.name || item.user.email || `#${item.user_id}` }}
                            </router-link>
                            <span v-else class="text-gray-500">#{{ item.user_id }}</span>
                        </td>
                        <td class="px-4 py-3 text-sm text-gray-500">
                            {{ item.user?.cpf || item.user?.cnpj || '—' }}
                        </td>
                        <td class="px-4 py-3 text-sm">
                            <span :class="statusClass(item.status)" class="rounded-full px-2 py-1 text-xs font-semibold">
                                {{ statusLabel(item.status) }}
                            </span>
                        </td>
                        <td class="px-4 py-3 text-sm text-gray-500">{{ item.ticket || '—' }}</td>
                        <td class="px-4 py-3 text-sm text-gray-500">{{ formatDate(item.submitted_at) }}</td>
                        <td class="px-4 py-3 text-sm text-right space-x-3">
                            <router-link
                                v-if="item.last_submission_id"
                                :to="{ name: 'fdic.detail', params: { id: item.last_submission_id } }"
                                class="text-indigo-500 hover:underline"
                            >Ver detalhes</router-link>
                            <router-link
                                :to="{ name: 'fdic.history', params: { userId: item.user_id } }"
                                class="text-gray-400 hover:underline text-xs"
                            >Histórico</router-link>
                        </td>
                    </tr>
                    <tr v-if="!items.length && !loading">
                        <td colspan="6" class="px-4 py-6 text-center text-sm text-gray-500">Nenhum cedente encontrado.</td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div class="mt-4 flex items-center justify-between">
            <button
                :disabled="pagination.current_page <= 1 || loading"
                @click="fetch(pagination.current_page - 1)"
                class="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm disabled:opacity-50 dark:bg-gray-800"
            >Anterior</button>
            <span class="text-sm">Página {{ pagination.current_page }} de {{ pagination.last_page }}</span>
            <button
                :disabled="pagination.current_page >= pagination.last_page || loading"
                @click="fetch(pagination.current_page + 1)"
                class="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm disabled:opacity-50 dark:bg-gray-800"
            >Próximo</button>
        </div>
    </div>
</template>

<script>
export default {
    name: "FdicSubmissions",
    data() {
        return {
            loading: false,
            items: [],
            filters: {
                status: "",
                document: "",
                from: "",
                to: "",
            },
            pagination: {
                current_page: 1,
                last_page: 1,
                per_page: 25,
            },
        };
    },
    mounted() {
        this.fetch(1);
    },
    methods: {
        async fetch(page = 1) {
            this.loading = true;
            try {
                const params = {
                    ...this.filters,
                    per_page: this.pagination.per_page,
                    page,
                };
                const { data } = await window.axios.get("/api/fdic/submissions/cedents", { params });
                this.items = data.data ?? data;
                this.pagination.current_page = data.current_page ?? page;
                this.pagination.last_page = data.last_page ?? 1;
            } finally {
                this.loading = false;
            }
        },
        clearFilters() {
            this.filters = { status: "", document: "", from: "", to: "" };
            this.fetch(1);
        },
        formatDate(iso) {
            if (!iso) return "—";
            return new Date(iso).toLocaleString("pt-BR");
        },
        statusLabel(status) {
            const map = {
                pending: "Pendente",
                processing: "Processando",
                approved: "Aprovado",
                approved_simple: "Aprovado Simples",
                approved_with_warnings: "Aprovado c/ Alertas",
                rejected: "Rejeitado",
                error: "Erro",
            };
            return map[status] ?? status;
        },
        statusClass(status) {
            return {
                "bg-yellow-100 text-yellow-800": status === "pending" || status === "processing",
                "bg-green-100 text-green-800": status === "approved" || status === "approved_simple",
                "bg-orange-100 text-orange-800": status === "approved_with_warnings",
                "bg-red-100 text-red-800": status === "rejected" || status === "error",
            };
        },
    },
};
</script>
